/**
 * PracticeEffectivenessCard — "What Regulates You"
 * Per-practice effectiveness from the adaptive n-of-1 engine:
 * mean regulation delta per practice, ranked, with calibration state.
 * Data: GET /api/training/practice-effectiveness/{userId}
 */
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PracticeEffectivenessCard({ userId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/training/practice-effectiveness/${userId}`)
      .then(r => setData(r.data))
      .catch(() => {});
  }, [userId]);

  if (!data) return null;

  const measured = (data.practices || []).filter(p => p.avg_regulation_delta != null);
  const isPersonalised = data.engine_state === 'personalised';

  // Scale bars against the strongest absolute delta (min 1 to avoid /0)
  const maxAbs = Math.max(1, ...measured.map(p => Math.abs(p.avg_regulation_delta)));

  return (
    <div style={{
      background: 'rgba(255,255,255,0.92)', borderRadius: '20px',
      padding: '24px', marginBottom: '16px',
      boxShadow: '0 4px 20px rgba(30,107,104,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
        <h3 style={{
          margin: 0, fontFamily: "'Playfair Display', serif",
          fontSize: '1.1rem', color: '#1E3A3A', fontWeight: '600'
        }}>What Regulates You</h3>
        <span style={{
          fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.06em',
          textTransform: 'uppercase', padding: '4px 10px', borderRadius: '12px',
          color: isPersonalised ? '#1E6B68' : '#8FA3A0',
          background: isPersonalised ? 'rgba(30,107,104,0.08)' : 'rgba(143,163,160,0.12)',
        }}>{isPersonalised ? 'Personalised' : 'Calibrating'}</span>
      </div>
      <p style={{ margin: '0 0 18px', fontSize: '0.78rem', color: '#8FA3A0', lineHeight: 1.5 }}>
        Average activation change per practice, measured across your own sessions.
        Positive bars mean the practice reliably brings you down.
      </p>

      {measured.length === 0 ? (
        <p style={{
          margin: 0, fontSize: '0.85rem', color: '#6A8F8C', lineHeight: 1.6,
          background: 'rgba(184,211,207,0.15)', borderRadius: '12px', padding: '14px 16px'
        }}>
          No measured sessions yet. Complete the Observe step after each practice —
          that's what teaches your engine which practices actually move your system.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {measured.map(p => {
            const d = p.avg_regulation_delta;
            const regulatesDown = d >= 0;
            const width = Math.max(6, (Math.abs(d) / maxAbs) * 100);
            return (
              <div key={p.practice_id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1E3A3A' }}>{p.name}</span>
                  <span style={{
                    fontSize: '0.78rem', fontWeight: '700',
                    color: regulatesDown ? '#1E6B68' : '#B8906A'
                  }}>
                    {regulatesDown ? '−' : '+'}{Math.abs(d).toFixed(1)} activation
                    <span style={{ color: '#B8C9C6', fontWeight: '500' }}> · {p.measured_sessions} session{p.measured_sessions !== 1 ? 's' : ''}</span>
                  </span>
                </div>
                <div style={{ height: '8px', background: 'rgba(184,211,207,0.25)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${width}%`, borderRadius: '4px',
                    background: regulatesDown
                      ? 'linear-gradient(90deg, #1E6B68 0%, #2D8B87 100%)'
                      : 'linear-gradient(90deg, #C9A57E 0%, #B8906A 100%)',
                    transition: 'width 0.6s ease',
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isPersonalised && measured.length > 0 && (
        <p style={{ margin: '14px 0 0', fontSize: '0.75rem', color: '#8FA3A0', lineHeight: 1.5 }}>
          Your engine personalises once at least two practices have 3+ measured sessions.
          Keep varying your practice — every Observe step sharpens the ranking.
        </p>
      )}
    </div>
  );
}
